import React, { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import LoadingOverlay from "../../components/common/LoadingOverlay";
import {
  registerUser,
  getAllEnabledProducts,
  getInvoiceState,
  validateCoupon,
  getSubscriptionStatus,
  loginUser,
} from "../../api";
import { handleApiError } from "../../utils/apiErrorHandler";
import {
  captureAffiliateFromSearch,
  getStoredAffiliateKey,
  getStoredMarketingKey,
} from "../../utils/affiliateTracking";
import { SCROLL_TO_PRICING_STATE } from "../../utils/scrollToPricing";
import useGoToPricing from "../../hooks/useGoToPricing";
import NFTPage from "../NFTPage";
import {
  RegisterAlert,
  RegisterApplyBtn,
  RegisterBitcoinNote,
  RegisterCard,
  RegisterCardGrid,
  RegisterCardHeader,
  RegisterCheckbox,
  RegisterCouponActions,
  RegisterCouponApplied,
  RegisterCouponAppliedText,
  RegisterCouponChange,
  RegisterCouponClear,
  RegisterCouponCode,
  RegisterCouponError,
  RegisterCouponField,
  RegisterCouponRemove,
  RegisterCouponRow,
  RegisterCouponSave,
  RegisterEmptyActions,
  RegisterEmptyIcon,
  RegisterEmptyState,
  RegisterEmptyText,
  RegisterEmptyTitle,
  RegisterField,
  RegisterForm,
  RegisterHeroBtn,
  RegisterHint,
  RegisterIncompleteHint,
  RegisterInput,
  RegisterInputIcon,
  RegisterInputSuffix,
  RegisterInputWrap,
  RegisterInvoiceFrame,
  RegisterInvoiceHeader,
  RegisterInvoicePanel,
  RegisterInvoiceWaiting,
  RegisterLabel,
  RegisterOutlineBtn,
  RegisterPayTab,
  RegisterPayTabs,
  RegisterPlanCard,
  RegisterPlanDesc,
  RegisterPlanDiscountLabel,
  RegisterPlanDiscountRow,
  RegisterPlanDiscountValue,
  RegisterPlanName,
  RegisterPlanPrice,
  RegisterPlanPriceCol,
  RegisterPlanPriceOld,
  RegisterPlanTop,
  RegisterSection,
  RegisterSectionHeading,
  RegisterSpinIcon,
  RegisterStepBadge,
  RegisterSubmit,
  RegisterSubmitBlock,
  RegisterSubtitle,
  RegisterTermsLabel,
  RegisterTermsLink,
  RegisterTermsRow,
  RegisterTitle,
  RegisterTotalLabel,
  RegisterTotalRow,
  RegisterTotalValue,
  RegisterTrust,
} from "./Register.styles";

const money = (n) =>
  `$${Number(n).toLocaleString("en-US", {
    minimumFractionDigits: Number(n) % 1 ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;

const formatCardNumber = (v) =>
  v
    .replace(/\D/g, "")
    .slice(0, 16) // prod placeholder / mask: #### #### #### ####
    .replace(/(.{4})/g, "$1 ")
    .trim();

const formatExpiry = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 4);
  return d.length <= 2 ? d : `${d.slice(0, 2)}/${d.slice(2)}`;
};

const UserIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const MailIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect width="20" height="16" x="2" y="4" rx="2" />
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </svg>
);

const LockIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const AlertCircleIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" x2="12" y1="8" y2="12" />
    <line x1="12" x2="12.01" y1="16" y2="16" />
  </svg>
);

const CheckIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

const XIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

const TagIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z" />
    <circle cx="7.5" cy="7.5" r=".5" fill="currentColor" />
  </svg>
);

const CreditCardIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect width="20" height="14" x="2" y="5" rx="2" />
    <line x1="2" x2="22" y1="10" y2="10" />
  </svg>
);

/** Lucide `Bitcoin` — matches Lovable Register.tsx */
const BitcoinIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M11.767 19.089c4.924.868 6.14-6.025 1.216-6.894m-1.216 6.894L5.86 18.047m5.908 1.042-.347 1.97m1.563-8.864c4.924.869 6.14-6.025 1.215-6.893m-1.215 6.893-3.94-.694m5.155-6.2L8.29 4.26m5.908 1.042.348-1.97M7.48 20.364l3.126-17.727" />
  </svg>
);

const WalletPayIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4" />
    <path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4" />
    <circle cx="17" cy="12" r="1" fill="currentColor" stroke="none" />
  </svg>
);

const ShieldCheckIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 13c0 5-3.5 7.5-8 9-4.5-1.5-8-4-8-9V5l8-3 8 3z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

const PackageOpenIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 22v-9" />
    <path d="M15.17 2.21a1.67 1.67 0 0 1 1.63 0L21 4.57a1.93 1.93 0 0 1 0 3.36L8.82 14.79a1.655 1.655 0 0 1-1.64 0L3 12.43a1.93 1.93 0 0 1 0-3.36z" />
    <path d="M20 13v3.87a2.06 2.06 0 0 1-1.11 1.83l-6 3.08a1.93 1.93 0 0 1-1.78 0l-6-3.08A2.06 2.06 0 0 1 4 16.87V13" />
    <path d="M21 12.43 8.82 18.79a1.655 1.655 0 0 1-1.64 0L3 15.57" />
  </svg>
);

const LoaderIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);

const NFT_LIFETIME_PRODUCT = {
  id: 5,
  name: "Lifetime",
  cost: 2997,
  billingCycle: 65535,
  maxActiveSubscriptions: null,
  maxQuestionsPerAskerPerCycle: null,
  order: 5,
  link: null,
  marketingPhrase: "One time purchase - lifetime access. Bitcoin Payment Only!",
  description:
    "Lifetime membership to Bitcoin Live.  One time purchase - lifetime access. \nBitcoin Payment Only Accepted!",
  capIds: "1,2,5",
  specificPublisherIds: null,
};

/**
 * Lovable Register checkout UI + prod RegisterForm behavior.
 */
const RegisterCheckout = ({
  planId: planIdProp,
  coupon: couponProp,
  skipScrollOnMount = false,
  title = "Create your account",
  subtitle = "Join bitcoin.live and complete your subscription.",
  headerAlign = "center",
  compactHeader = false,
  submitLabel = "Complete your order",
  termsSuffix = "",
  trustText = "Secure checkout — your payment details are encrypted.",
  compactTrust = false,
} = {}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const goToPricing = useGoToPricing();
  const pollRef = useRef(null);

  const isNftPath =
    location.pathname.includes("NFT") || location.pathname.includes("nft");

  const [ready, setReady] = useState(false);
  const [gotoPrices, setGotoPrices] = useState(false);
  const [products, setProducts] = useState([]);
  const [planId, setPlanId] = useState(null);

  const [displayName, setDisplayName] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [checked, setChecked] = useState(false);

  const [activeTab, setActiveTab] = useState("cc");
  const [ownerId, setOwnerId] = useState("");

  const [coupon, setCoupon] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [discount, setDiscount] = useState(null);
  const [couponApplying, setCouponApplying] = useState(false);
  const [showCouponError, setShowCouponError] = useState(false);
  const [couponError, setCouponError] = useState("");

  const [ccName, setCcName] = useState("");
  const [ccNumber, setCcNumber] = useState("");
  const [ccExpiration, setCcExpiration] = useState("");
  const [ccCVV, setCcCVV] = useState("");

  const [loading, setLoading] = useState(false);
  const [formSubmitBtnDisabled, setFormSubmitBtnDisabled] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [btcPaymentUrl, setBtcPaymentUrl] = useState(null);

  const clearPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => () => clearPoll(), [clearPoll]);

  const applyCoupon = useCallback(
    async (codeOverride, productIdOverride) => {
      const code = (codeOverride ?? coupon ?? "").trim();
      const productId = productIdOverride ?? planId;
      if (!code || couponApplying || !productId) {
        return;
      }

      setCouponApplying(true);
      setShowCouponError(false);
      setCouponError("");

      try {
        const resp = await validateCoupon({
          couponName: code,
          productIds: [productId],
        });

        const result = resp?.data?.[0];
        if (result?.applicable) {
          setCoupon(code);
          setAppliedCoupon(code);
          setDiscount(result.discountedCost);
          setShowCouponError(false);
          setCouponApplying(false);
        } else {
          setAppliedCoupon(null);
          setDiscount(null);
          setShowCouponError(true);
          setCouponError(
            result?.reason || "This coupon is not valid for this plan."
          );
          setCouponApplying(false);
        }
      } catch {
        setAppliedCoupon(null);
        setDiscount(null);
        setShowCouponError(true);
        setCouponError("Could not validate coupon. Please try again.");
        setCouponApplying(false);
      }
    },
    [coupon, couponApplying, planId]
  );

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      const productsResp = await getAllEnabledProducts();
      if (cancelled) return;

      const list = Array.isArray(productsResp?.data)
        ? [...productsResp.data]
        : [];

      if (isNftPath) {
        list.push(NFT_LIFETIME_PRODUCT);
      }

      const search = window.location.search;
      captureAffiliateFromSearch(search);
      const params = new URLSearchParams(search);
      const planIdFromUrl = parseInt(params.get("planId"), 10);
      const couponFromUrl = params.get("coupon");

      if (isNaN(planIdFromUrl) && !planIdProp && !isNftPath) {
        setGotoPrices(true);
        setReady(true);
        return;
      }

      const resolvedPlanId = isNftPath
        ? 5
        : planIdProp
          ? planIdProp
          : planIdFromUrl;

      setPlanId(resolvedPlanId);
      setProducts(list);
      if (!skipScrollOnMount) {
        window.scrollTo(0, 0);
      }
      setReady(true);

      const initialCoupon = couponFromUrl || couponProp;
      if (initialCoupon) {
        setCoupon(initialCoupon);
        await applyCoupon(initialCoupon, resolvedPlanId);
      }
    };

    init();
    return () => {
      cancelled = true;
    };
    // Mount-only init (mirrors RegisterForm componentDidMount).
  }, []);

  const getSelectedPlan = () => {
    if (!planId || !products.length) return null;
    return products.filter((p) => p.id === planId)[0] || null;
  };

  const getCouponSavings = () => {
    const plan = getSelectedPlan();
    if (!plan || discount == null) return null;
    const savings = plan.cost - discount;
    if (savings <= 0) return null;
    return Math.round(savings * 100) / 100;
  };

  const hasAppliedCoupon = Boolean(appliedCoupon && discount != null);

  const isCreditCardComplete = () => {
    const name = (ccName || "").trim();
    const number = (ccNumber || "").replace(/\s+/g, "");
    const expiration = (ccExpiration || "").trim();
    const cvc = (ccCVV || "").replace(/\s+/g, "");

    return (
      name.length > 0 &&
      number.length >= 15 &&
      /^\d{2}\/\d{2}$/.test(expiration) &&
      cvc.length >= 3 &&
      cvc.length <= 4
    );
  };

  const isPaymentComplete = () => {
    if (isNftPath) {
      return Boolean(ownerId);
    }
    if (activeTab === "cc") {
      return isCreditCardComplete();
    }
    return true;
  };

  const isSubmitDisabled =
    formSubmitBtnDisabled ||
    displayName === "" ||
    username === "" ||
    password === "" ||
    !checked ||
    !isPaymentComplete();

  const registerRedirect = async (session) => {
    const paymentMethod = activeTab === "cc" ? 1 : 2;
    const userData = {
      name: displayName,
      email: username,
      password,
      productId: planId,
      paymentMethodId: !isNftPath ? paymentMethod : 5,
    };
    const subscriptionStatus = await getSubscriptionStatus(session.userId);
    navigate("/blogs/premium/", {
      state: {
        details: userData,
        products,
        renewal:
          subscriptionStatus &&
          subscriptionStatus.data[0].subscription.nextRenewal.date,
      },
    });
  };

  const getProfile = async (session) => {
    fetch(`${process.env.REACT_APP_API_URL}user/${session.userId}`, {
      headers: {
        "Content-Type": "application/json",
        "X-Auth-Token": session.token,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        localStorage.setItem("user", JSON.stringify(data));
        localStorage.setItem("session", JSON.stringify(session.token));
        registerRedirect(session);
      });
  };

  const startInvoicePoll = (paymentKey) => {
    clearPoll();
    pollRef.current = setInterval(async () => {
      const invoiceStatus = await getInvoiceState(paymentKey);
      if (invoiceStatus && invoiceStatus.data.paymentStatusId === 5) {
        setLoading(false);
        setBtcPaymentUrl(null);
        const res = await loginUser({
          email: username,
          password,
        });
        getProfile(res && res.data);
        clearPoll();
      } else if (invoiceStatus && invoiceStatus.data.paymentStatusId === 4) {
        clearPoll();
        setShowError(true);
        setErrorText(
          activeTab === "bitcoin" && !isNftPath
            ? "Failed"
            : invoiceStatus.data.error
        );
        setFormSubmitBtnDisabled(false);
        setBtcPaymentUrl(null);
        setLoading(false);
      }
    }, 3000);
  };

  const register = async () => {
    setShowError(false);
    setFormSubmitBtnDisabled(true);
    const paymentMethod = activeTab === "cc" ? 1 : 2;
    const data = {
      name: displayName,
      email: username,
      password,
      productId: planId,
      paymentMethodId: !isNftPath ? paymentMethod : 5,
    };

    if (isNftPath) {
      data.nftDetails = { walletAddress: ownerId };
    }

    if (paymentMethod === 1 && !isNftPath) {
      data.creditCard = {
        number: ccNumber.replace(/\s+/g, ""),
        expirationDate: `20${
          ccExpiration && ccExpiration.slice(3, 5)
        }-${ccExpiration && ccExpiration.slice(0, 2)}`,
        cvc: ccCVV,
      };
    }

    if (appliedCoupon) {
      data.couponName = appliedCoupon;
    }

    const affiliate = getStoredAffiliateKey();
    if (affiliate) {
      data.inviterAffiliateKey = affiliate;
    }

    const marketingSource = getStoredMarketingKey();
    if (marketingSource) {
      data.marketingSourceKey = marketingSource;
    }

    const userResp = await registerUser(data);
    if (userResp && userResp.status >= 200 && userResp.status < 300) {
      if (paymentMethod === 1 || isNftPath) {
        if (userResp) {
          const getUrl = await getInvoiceState(userResp.data.paymentKey);
          if (getUrl.data.redirectUrl) {
            setBtcPaymentUrl(getUrl.data.redirectUrl);
          } else {
            setLoading(true);
          }
          setTimeout(() => {
            const size = document.body.scrollHeight - 150;
            window.scrollTo(0, size);
          }, 1500);
          startInvoicePoll(userResp.data.paymentKey);
        } else {
          setFormSubmitBtnDisabled(false);
          setLoading(false);
          const res = await loginUser({
            email: username,
            password,
          });
          getProfile(res && res.data);
        }
      } else if (paymentMethod === 2 && !isNftPath) {
        const getUrl = await getInvoiceState(userResp.data.paymentKey);
        setBtcPaymentUrl(getUrl.data.redirectUrl);
        setTimeout(() => {
          const size = document.body.scrollHeight - 150;
          window.scrollTo(0, size);
        }, 1500);
        startInvoicePoll(userResp.data.paymentKey);
      }
    } else {
      const errorResponse = handleApiError(userResp, "Registration failed", {
        notify: false,
      });
      setShowError(true);
      setErrorText(errorResponse.message);
      setFormSubmitBtnDisabled(false);
    }
  };

  const removeCoupon = () => {
    setCoupon("");
    setAppliedCoupon(null);
    setDiscount(null);
    setShowCouponError(false);
    setCouponError("");
    setCouponApplying(false);
  };

  const changeCouponCode = () => {
    const previousCode = appliedCoupon || "";
    setAppliedCoupon(null);
    setDiscount(null);
    setCoupon(previousCode);
    setShowCouponError(false);
    setCouponError("");
    setCouponApplying(false);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (isSubmitDisabled) return;
    register();
  };

  if (gotoPrices) {
    return <Navigate to="/" replace state={SCROLL_TO_PRICING_STATE} />;
  }

  const selectedPlan = getSelectedPlan();
  const couponSavings = getCouponSavings();
  const couponTrimmed = (coupon || "").trim();
  const totalDue =
    selectedPlan == null
      ? 0
      : hasAppliedCoupon
        ? discount
        : selectedPlan.cost;
  const cardDigits = ccNumber.replace(/\s/g, "").length;
  // Lovable empty state only when no planId (prod redirects via gotoPrices).
  // If planId is set but product isn't in the list yet, keep the form like prod.
  const showEmptyPlan = ready && !planId && !isNftPath;

  return (
    <LoadingOverlay active={loading} spinner text="Processing...">
      <RegisterCard>
        <RegisterCardHeader $align={headerAlign} $compact={compactHeader}>
          <RegisterTitle as={compactHeader ? "h2" : "h1"} $compact={compactHeader}>
            {title}
          </RegisterTitle>
          <RegisterSubtitle $compact={compactHeader}>{subtitle}</RegisterSubtitle>
        </RegisterCardHeader>

        {showEmptyPlan ? (
          <RegisterEmptyState>
            <RegisterEmptyIcon>
              <PackageOpenIcon />
            </RegisterEmptyIcon>
            <RegisterEmptyTitle>No plan selected</RegisterEmptyTitle>
            <RegisterEmptyText>
              Pick a subscription first and we&apos;ll bring you straight back
              here to finish creating your account.
            </RegisterEmptyText>
            <RegisterEmptyActions>
              <RegisterHeroBtn type="button" onClick={goToPricing}>
                View plans
              </RegisterHeroBtn>
              <RegisterOutlineBtn to="/login">
                I already have an account
              </RegisterOutlineBtn>
            </RegisterEmptyActions>
          </RegisterEmptyState>
        ) : (
          <RegisterForm onSubmit={onSubmit} noValidate>
            {showError ? (
              <RegisterAlert role="alert">
                <AlertCircleIcon />
                <span>{errorText}</span>
              </RegisterAlert>
            ) : null}

            <RegisterSection>
              <RegisterSectionHeading>
                <RegisterStepBadge>1</RegisterStepBadge>
                Account details
              </RegisterSectionHeading>

              <RegisterField>
                <RegisterLabel htmlFor="register-display-name">
                  Display name
                </RegisterLabel>
                <RegisterInputWrap>
                  <RegisterInputIcon>
                    <UserIcon />
                  </RegisterInputIcon>
                  <RegisterInput
                    id="register-display-name"
                    $withIcon
                    autoComplete="name"
                    placeholder="Your name"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    disabled={formSubmitBtnDisabled}
                  />
                </RegisterInputWrap>
              </RegisterField>

              <RegisterField>
                <RegisterLabel htmlFor="register-email">Email</RegisterLabel>
                <RegisterInputWrap>
                  <RegisterInputIcon>
                    <MailIcon />
                  </RegisterInputIcon>
                  <RegisterInput
                    id="register-email"
                    $withIcon
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    disabled={formSubmitBtnDisabled}
                  />
                </RegisterInputWrap>
              </RegisterField>

              <RegisterField>
                <RegisterLabel htmlFor="register-password">
                  Password
                </RegisterLabel>
                <RegisterInputWrap>
                  <RegisterInputIcon>
                    <LockIcon />
                  </RegisterInputIcon>
                  <RegisterInput
                    id="register-password"
                    $withIcon
                    type="password"
                    autoComplete="new-password"
                    placeholder="Choose a password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={formSubmitBtnDisabled}
                  />
                </RegisterInputWrap>
              </RegisterField>

              <RegisterTermsRow>
                <RegisterCheckbox
                  type="button"
                  id="register-terms"
                  role="checkbox"
                  aria-checked={checked}
                  aria-label="Accept Terms and Conditions"
                  disabled={formSubmitBtnDisabled}
                  onClick={() => setChecked((v) => !v)}
                  onKeyDown={(e) => {
                    if (e.key === " " || e.key === "Enter") {
                      e.preventDefault();
                      setChecked((v) => !v);
                    }
                  }}
                >
                  {checked ? <CheckIcon /> : null}
                </RegisterCheckbox>
                <RegisterTermsLabel htmlFor="register-terms">
                  I agree to the{" "}
                  <RegisterTermsLink
                    to="/terms&conditions"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Terms and Conditions
                  </RegisterTermsLink>
                  {termsSuffix}
                </RegisterTermsLabel>
              </RegisterTermsRow>
            </RegisterSection>

            <RegisterSection>
              <RegisterSectionHeading>
                <RegisterStepBadge>2</RegisterStepBadge>
                Your plan
              </RegisterSectionHeading>

              {selectedPlan ? (
                <RegisterPlanCard>
                  <RegisterPlanTop>
                    <div>
                      <RegisterPlanName>{selectedPlan.name}</RegisterPlanName>
                      <RegisterPlanDesc>
                        {selectedPlan.description}
                      </RegisterPlanDesc>
                    </div>
                    {!isNftPath ? (
                      <RegisterPlanPriceCol>
                        {hasAppliedCoupon ? (
                          <RegisterPlanPriceOld>
                            {money(selectedPlan.cost)}
                          </RegisterPlanPriceOld>
                        ) : null}
                        <RegisterPlanPrice>
                          <span>
                            {money(hasAppliedCoupon ? discount : selectedPlan.cost)}
                          </span>
                        </RegisterPlanPrice>
                      </RegisterPlanPriceCol>
                    ) : null}
                  </RegisterPlanTop>

                  {!isNftPath && hasAppliedCoupon && couponSavings != null ? (
                    <RegisterPlanDiscountRow>
                      <RegisterPlanDiscountLabel>Discount</RegisterPlanDiscountLabel>
                      <RegisterPlanDiscountValue>
                        You save {money(couponSavings)}
                      </RegisterPlanDiscountValue>
                    </RegisterPlanDiscountRow>
                  ) : null}
                </RegisterPlanCard>
              ) : null}

              {!isNftPath ? (
                hasAppliedCoupon ? (
                  <RegisterCouponApplied>
                    <RegisterCouponAppliedText>
                      <CheckCircleIcon />
                      <span>
                        Coupon{" "}
                        <RegisterCouponCode>{appliedCoupon}</RegisterCouponCode>{" "}
                        applied
                        {couponSavings != null ? (
                          <>
                            {" "}
                            — you save{" "}
                            <RegisterCouponSave>
                              {money(couponSavings)}
                            </RegisterCouponSave>
                          </>
                        ) : null}
                      </span>
                    </RegisterCouponAppliedText>
                    <RegisterCouponActions>
                      <RegisterCouponChange
                        type="button"
                        onClick={changeCouponCode}
                      >
                        Change code
                      </RegisterCouponChange>
                      <RegisterCouponRemove
                        type="button"
                        aria-label="Remove coupon"
                        onClick={removeCoupon}
                      >
                        <XIcon />
                      </RegisterCouponRemove>
                    </RegisterCouponActions>
                  </RegisterCouponApplied>
                ) : (
                  <div>
                    <RegisterCouponRow>
                      <RegisterCouponField>
                        <RegisterInputIcon>
                          <TagIcon />
                        </RegisterInputIcon>
                        <RegisterInput
                          $withIcon
                          $withClear={Boolean(coupon.length)}
                          aria-label="Coupon code"
                          aria-invalid={showCouponError ? "true" : undefined}
                          placeholder="Coupon code (optional)"
                          value={coupon}
                          onChange={(e) => {
                            setCoupon(e.target.value);
                            if (showCouponError) {
                              setShowCouponError(false);
                              setCouponError("");
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              applyCoupon();
                            }
                          }}
                          disabled={formSubmitBtnDisabled || couponApplying}
                        />
                        {coupon.length > 0 ? (
                          <RegisterCouponClear
                            type="button"
                            aria-label="Clear coupon code"
                            onClick={() => {
                              setCoupon("");
                              setShowCouponError(false);
                              setCouponError("");
                            }}
                            disabled={formSubmitBtnDisabled || couponApplying}
                          >
                            <XIcon />
                          </RegisterCouponClear>
                        ) : null}
                      </RegisterCouponField>
                      <RegisterApplyBtn
                        type="button"
                        onClick={() => applyCoupon()}
                        disabled={
                          formSubmitBtnDisabled ||
                          !couponTrimmed ||
                          couponApplying
                        }
                      >
                        {couponApplying ? "Applying…" : "Apply"}
                      </RegisterApplyBtn>
                    </RegisterCouponRow>
                    {showCouponError ? (
                      <RegisterCouponError>
                        <AlertCircleIcon />
                        {couponError}
                      </RegisterCouponError>
                    ) : null}
                  </div>
                )
              ) : null}
            </RegisterSection>

            <RegisterSection>
              <RegisterSectionHeading>
                <RegisterStepBadge>3</RegisterStepBadge>
                Payment
              </RegisterSectionHeading>

              <RegisterPayTabs
                role="tablist"
                aria-label="Payment method"
                $single={isNftPath}
              >
                {isNftPath ? (
                  <RegisterPayTab type="button" aria-selected="true" disabled>
                    <WalletPayIcon />
                    NFT
                  </RegisterPayTab>
                ) : (
                  <>
                    <RegisterPayTab
                      type="button"
                      role="tab"
                      aria-selected={activeTab === "cc"}
                      onClick={() => setActiveTab("cc")}
                    >
                      <CreditCardIcon />
                      Credit Card
                    </RegisterPayTab>
                    <RegisterPayTab
                      type="button"
                      role="tab"
                      aria-selected={activeTab === "bitcoin"}
                      onClick={() => setActiveTab("bitcoin")}
                    >
                      <BitcoinIcon />
                      Bitcoin
                    </RegisterPayTab>
                  </>
                )}
              </RegisterPayTabs>

              {isNftPath ? (
                <NFTPage setownerId={(value) => setOwnerId(value)} />
              ) : null}

              {activeTab === "cc" && !isNftPath ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                  <RegisterField>
                    <RegisterLabel htmlFor="register-cc-name">
                      Cardholder name
                    </RegisterLabel>
                    <RegisterInputWrap>
                      <RegisterInputIcon>
                        <UserIcon />
                      </RegisterInputIcon>
                      <RegisterInput
                        id="register-cc-name"
                        $withIcon
                        autoComplete="cc-name"
                        placeholder="Name on card"
                        value={ccName}
                        onChange={(e) => setCcName(e.target.value)}
                        disabled={formSubmitBtnDisabled}
                      />
                    </RegisterInputWrap>
                  </RegisterField>

                  <RegisterField>
                    <RegisterLabel htmlFor="register-cc-number">
                      Card number
                    </RegisterLabel>
                    <RegisterInputWrap>
                      <RegisterInputIcon>
                        <CreditCardIcon />
                      </RegisterInputIcon>
                      <RegisterInput
                        id="register-cc-number"
                        $withIcon
                        $withSuffix
                        $mono
                        $trackingWide
                        inputMode="numeric"
                        autoComplete="cc-number"
                        placeholder="1234 5678 9012 3456"
                        value={ccNumber}
                        onChange={(e) =>
                          setCcNumber(formatCardNumber(e.target.value))
                        }
                        disabled={formSubmitBtnDisabled}
                      />
                      <RegisterInputSuffix>{cardDigits}/16</RegisterInputSuffix>
                    </RegisterInputWrap>
                    <RegisterHint>Grouped in 4s as you type.</RegisterHint>
                  </RegisterField>

                  <RegisterCardGrid className="exp-cvc">
                    <RegisterField>
                      <RegisterLabel htmlFor="register-cc-exp">
                        Expiration
                      </RegisterLabel>
                      <RegisterInput
                        id="register-cc-exp"
                        $mono
                        $trackingWidest
                        inputMode="numeric"
                        autoComplete="cc-exp"
                        placeholder="MM/YY"
                        value={ccExpiration}
                        onChange={(e) =>
                          setCcExpiration(formatExpiry(e.target.value))
                        }
                        disabled={formSubmitBtnDisabled}
                      />
                      <RegisterHint>Format MM/YY</RegisterHint>
                    </RegisterField>

                    <RegisterField>
                      <RegisterLabel htmlFor="register-cc-cvc">CVC</RegisterLabel>
                      <RegisterInputWrap>
                        <RegisterInputIcon>
                          <LockIcon />
                        </RegisterInputIcon>
                        <RegisterInput
                          id="register-cc-cvc"
                          $withIcon
                          $mono
                          $trackingWidest
                          inputMode="numeric"
                          autoComplete="cc-csc"
                          placeholder="•••"
                          value={ccCVV}
                          onChange={(e) =>
                            setCcCVV(
                              e.target.value.replace(/\D/g, "").slice(0, 4)
                            )
                          }
                          disabled={formSubmitBtnDisabled}
                        />
                      </RegisterInputWrap>
                      <RegisterHint>3 digits on the back</RegisterHint>
                    </RegisterField>
                  </RegisterCardGrid>

                  {btcPaymentUrl ? (
                    <RegisterInvoicePanel>
                      <RegisterInvoiceHeader>
                        Payment verification
                      </RegisterInvoiceHeader>
                      <RegisterInvoiceFrame>
                        <iframe
                          title="Payment verification"
                          src={btcPaymentUrl}
                        />
                      </RegisterInvoiceFrame>
                      <RegisterInvoiceWaiting>
                        <RegisterSpinIcon>
                          <LoaderIcon />
                        </RegisterSpinIcon>
                        Waiting for payment confirmation…
                      </RegisterInvoiceWaiting>
                    </RegisterInvoicePanel>
                  ) : null}
                </div>
              ) : null}

              {activeTab === "bitcoin" && !isNftPath ? (
                btcPaymentUrl ? (
                  <RegisterInvoicePanel>
                    <RegisterInvoiceHeader>
                      <BitcoinIcon />
                      Bitcoin invoice
                    </RegisterInvoiceHeader>
                    <RegisterInvoiceFrame>
                      <iframe title="Bitcoin payment" src={btcPaymentUrl} />
                    </RegisterInvoiceFrame>
                    <RegisterInvoiceWaiting>
                      <RegisterSpinIcon>
                        <LoaderIcon />
                      </RegisterSpinIcon>
                      Waiting for payment — your access unlocks after
                      confirmation.
                    </RegisterInvoiceWaiting>
                  </RegisterInvoicePanel>
                ) : (
                  <RegisterBitcoinNote>
                    <BitcoinIcon />
                    <p>
                      After you complete your order, we&apos;ll show a Bitcoin
                      invoice with the amount and address to pay.
                    </p>
                  </RegisterBitcoinNote>
                )
              ) : null}
            </RegisterSection>

            <RegisterSubmitBlock>
              {selectedPlan && !isNftPath ? (
                <RegisterTotalRow>
                  <RegisterTotalLabel>Total due today</RegisterTotalLabel>
                  <RegisterTotalValue>{money(totalDue)}</RegisterTotalValue>
                </RegisterTotalRow>
              ) : null}

              <RegisterSubmit type="submit" disabled={isSubmitDisabled}>
                {formSubmitBtnDisabled && !btcPaymentUrl ? (
                  <>
                    <RegisterSpinIcon>
                      <LoaderIcon />
                    </RegisterSpinIcon>
                    Processing…
                  </>
                ) : (
                  submitLabel
                )}
              </RegisterSubmit>

              {isSubmitDisabled && !formSubmitBtnDisabled ? (
                <RegisterIncompleteHint>
                  Complete the fields above to enable checkout.
                </RegisterIncompleteHint>
              ) : null}

              <RegisterTrust $compact={compactTrust}>
                <ShieldCheckIcon />
                <span>{trustText}</span>
              </RegisterTrust>
            </RegisterSubmitBlock>
          </RegisterForm>
        )}
      </RegisterCard>
    </LoadingOverlay>
  );
};

export default RegisterCheckout;
